import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Select, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";

import {
  getAllEmployeesListDataByCompanyId,
  getLeaveTypes,
  listEmployeeBalances,
} from "../../../../../../services/attendanceService";
import { getErrorMessage } from "../../../../../../core/data/redux/utils/errorUtils";
import { notify } from "../../../../../../services/toast.config";
import { all_routes } from "../../../../../router/all_routes";

import AdjustBalanceModal from "./modals/AdjustBalanceModal";
import EmployeeAdjustmentModal from "./modals/EmployeeAdjustmentModal";
import AntdDataTable from "./components/AntdDataTable";

import type {
  EmployeeBalanceRow,
  EmployeeOption,
  LeaveTypeOption,
  Pagination,
} from "./types";

type SelectedCompany = {
  value: string;
  label: string;
  type: string;
} | null;

type AttendanceBalanceTabProps = {
  selectedCompany: SelectedCompany;
};

const currentYear = new Date().getFullYear();

const DEFAULT_PAGINATION: Pagination = {
  total: 0,
  current_page: 1,
  per_page: 20,
  total_pages: 0,
};

export const AttendanceBalanceTab: React.FC<AttendanceBalanceTabProps> = ({
  selectedCompany,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<EmployeeBalanceRow[]>([]);
  const [pagination, setPagination] =
    useState<Pagination>(DEFAULT_PAGINATION);

  const [employees, setEmployees] = useState<EmployeeOption[]>([
    { value: "", label: "All Employees" },
  ]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeOption[]>([]);

  const [tempEmployeeId, setTempEmployeeId] = useState<string>("");
  const [tempLeaveTypeId, setTempLeaveTypeId] = useState<number | null>(null);

  const [filters, setFilters] = useState<{
    employeeId: string;
    leaveTypeId: number | null;
    year: number;
  }>({ employeeId: "", leaveTypeId: null, year: currentYear });

  const [activeRow, setActiveRow] = useState<EmployeeBalanceRow | null>(null);
  const [activeView, setActiveView] = useState<"adjust" | "history" | null>(
    null,
  );

  useEffect(() => {
    if (!selectedCompany?.value) {
      setEmployees([{ value: "", label: "All Employees" }]);
      setLeaveTypes([]);
      setRows([]);
      return;
    }
    fetchEmployees();
    fetchLeaveTypes();
  }, [selectedCompany?.value]);

  useEffect(() => {
    if (!selectedCompany?.value) return;
    fetchRows(1, pagination.per_page, filters);
  }, [selectedCompany?.value, filters]);

  const fetchEmployees = async () => {
    if (!selectedCompany) return;
    try {
      const res = await getAllEmployeesListDataByCompanyId({
        company_id: selectedCompany.value,
        company_type: selectedCompany.type,
      });
      const fetched = res?.data?.data?.allUsers || [];
      setEmployees([
        { value: "", label: "All Employees" },
        ...fetched.map((e: any) => ({
          value: e.id.toString(),
          label: `${e.salutation || ""} ${e.fname} ${e.lname}`.trim(),
        })),
      ]);
    } catch {
      notify.error("Failed to load employees");
      setEmployees([{ value: "", label: "All Employees" }]);
    }
  };

  const fetchLeaveTypes = async () => {
    if (!selectedCompany) return;
    try {
      const res = await getLeaveTypes(selectedCompany);
      const list: any[] = res?.data?.data?.leaveTypes || [];
      setLeaveTypes(
        list
          .filter((lt) => lt.balance !== -1)
          .map((lt) => ({
            id: lt.id,
            name: lt.name,
            balance: lt.balance,
            isMonthlyAccrual: lt.isMonthlyAccrual,
            monthlyCarryForward: lt.monthlyCarryForward,
          })),
      );
    } catch {
      notify.error("Failed to load leave types");
      setLeaveTypes([]);
    }
  };

  const fetchRows = async (
    page: number,
    perPage: number,
    f: { employeeId: string; leaveTypeId: number | null; year: number },
  ) => {
    if (!selectedCompany?.value) return;
    setLoading(true);
    try {
      const payload: Record<string, any> = {
        company_id: parseInt(selectedCompany.value, 10),
        company_type: selectedCompany.type,
        year: f.year,
        page,
        per_page: perPage,
      };
      if (f.employeeId) payload.user_id = parseInt(f.employeeId, 10);
      if (f.leaveTypeId) payload.leave_type_id = f.leaveTypeId;

      const res = await listEmployeeBalances(payload);
      const body = res?.data?.data || {};
      setRows(body.data || []);
      setPagination(body.pagination || DEFAULT_PAGINATION);
    } catch (err) {
      notify.error(getErrorMessage(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setFilters({
      employeeId: tempEmployeeId,
      leaveTypeId: tempLeaveTypeId,
      year: currentYear,
    });
  };

  const resetFilters = () => {
    setTempEmployeeId("");
    setTempLeaveTypeId(null);
    setFilters({ employeeId: "", leaveTypeId: null, year: currentYear });
  };

  const openAdjust = (row: EmployeeBalanceRow) => {
    setActiveRow(row);
    setActiveView("adjust");
  };

  const openHistory = (row: EmployeeBalanceRow) => {
    setActiveRow(row);
    setActiveView("history");
  };

  const closeOverlays = () => {
    setActiveRow(null);
    setActiveView(null);
  };

  const onAdjustmentSuccess = () => {
    fetchRows(pagination.current_page, pagination.per_page, filters);
  };

  const columns: ColumnsType<EmployeeBalanceRow> = [
    {
      title: "Employee",
      dataIndex: "userName",
      key: "userName",
      render: (v: string, record) => (
        <div>
          <div className="fw-semibold">{v}</div>
          <div className="text-muted small">User #{record.userId}</div>
        </div>
      ),
    },
    {
      title: "Leave Type",
      dataIndex: "leaveTypeName",
      key: "leaveTypeName",
      render: (v: string, record) => (
        <div>
          <div className="fw-semibold">{v}</div>
          {record.isMonthlyAccrual ? (
            <Tag color="blue" style={{ marginTop: 2 }}>
              Monthly{record.monthlyCarryForward ? " · Carry" : ""}
            </Tag>
          ) : (
            <Tag color="default" style={{ marginTop: 2 }}>
              Yearly
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Year Total",
      dataIndex: "yearlyTotal",
      key: "yearlyTotal",
      width: 110,
      align: "right",
      render: (v: number) => (v ?? 0).toFixed(1),
    },
    {
      title: "Allocated YTD",
      dataIndex: "allocatedYTD",
      key: "allocatedYTD",
      width: 130,
      align: "right",
      render: (v: number, record) => (
        <Tooltip
          title={
            record.isMonthlyAccrual
              ? "Sum of monthly accrual through current month"
              : "Yearly allocation"
          }
        >
          {(v ?? 0).toFixed(1)}
        </Tooltip>
      ),
    },
    {
      title: "Used",
      dataIndex: "used",
      key: "used",
      width: 90,
      align: "right",
      render: (v: number) => (
        <Tag color={v > 0 ? "orange" : "default"}>{v.toFixed(1)}</Tag>
      ),
    },
    {
      title: "Available",
      dataIndex: "available",
      key: "available",
      width: 110,
      align: "right",
      render: (v: number, record) => (
        <Tooltip
          title={
            record.isMonthlyAccrual
              ? record.monthlyCarryForward
                ? "Effective max the employee can apply right now: min(latest month balance, monthlyLimit − used this month). Includes carry-forward chain."
                : "Effective max the employee can apply right now: min(latest month balance, monthlyLimit − used this month). Past months expired."
              : "Annual balance minus used"
          }
        >
          <Tag color={v > 0 ? "green" : "red"}>{v.toFixed(1)}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Last Adjusted",
      dataIndex: "lastAdjustedAt",
      key: "lastAdjustedAt",
      width: 150,
      render: (v: string | null) =>
        v ? (
          <Tooltip title={moment(v).format("DD MMM YYYY HH:mm")}>
            {moment(v).fromNow()}
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_: any, record) => (
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-info"
            onClick={() => openAdjust(record)}
          >
            Adjust
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            onClick={() => openHistory(record)}
          >
            History
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1">Leaves Balance</h5>
          <p className="text-muted mb-0 small">
            Manually adjust an employee's leave balance. Every change is
            audited.
          </p>
        </div>
        <Link
          to={all_routes.leaveBalanceAuditLog}
          className="btn btn-info"
        >
          <i className="ti ti-history me-2" />
          View Audit Log
        </Link>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body bg-primary-transparent">
          <div className="d-flex flex-wrap gap-3 align-items-end">
            <div style={{ minWidth: 240, flex: 1 }}>
              <div className="text-muted small mb-1">Employee</div>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                value={tempEmployeeId || undefined}
                onChange={(v) => setTempEmployeeId(v || "")}
                options={employees.filter((e) => e.value !== "")}
                placeholder="All employees"
                style={{ width: "100%" }}
                disabled={!selectedCompany?.value}
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <div className="text-muted small mb-1">Leave Type</div>
              <Select
                allowClear
                value={tempLeaveTypeId || undefined}
                onChange={(v) => setTempLeaveTypeId(v ?? null)}
                options={leaveTypes.map((lt) => ({
                  value: lt.id,
                  label: lt.name,
                }))}
                placeholder="All types"
                style={{ width: "100%" }}
                disabled={!selectedCompany?.value}
              />
            </div>
            <div className="d-flex gap-2 ms-auto">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={resetFilters}
              >
                Reset
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={applyFilters}
                disabled={!selectedCompany?.value}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>

      {!selectedCompany?.value ? (
        <div className="text-center py-5 text-muted">
          Select a company to view employee balances.
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0 mb-3">
            <AntdDataTable<EmployeeBalanceRow>
              columns={columns}
              dataSource={rows}
              rowKey={(record) => `${record.userId}:${record.leaveTypeId}`}
              loading={loading}
              pagination={pagination}
              onPageChange={(page, pageSize) =>
                fetchRows(page, pageSize, filters)
              }
              scrollX={1100}
              emptyText="No balances found for the selected filters"
            />
          </div>
        </div>
      )}

      <AdjustBalanceModal
        open={activeView === "adjust" && !!activeRow}
        row={activeRow}
        onClose={closeOverlays}
        onSuccess={onAdjustmentSuccess}
      />

      <EmployeeAdjustmentModal
        open={activeView === "history" && !!activeRow}
        userId={activeRow?.userId || null}
        userName={activeRow?.userName || null}
        leaveTypeId={activeRow?.leaveTypeId || null}
        leaveTypeName={activeRow?.leaveTypeName || null}
        year={activeRow?.year || filters.year}
        onClose={closeOverlays}
      />
    </>
  );
};

export default AttendanceBalanceTab;
